/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Icon, Text } from '@components/atoms'
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Button,
	TextField,
	IconButton,
	Link,
	Tooltip,
} from '@mui/material'
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { Bom, BomType } from '@schemas'
import { blue, grey } from '@mui/material/colors'
import { Decimal } from '@utils'

interface BOMListProps {
	boms: BomType[]
	isEditMode: boolean
	onUpdateBOM: (updatedBOM: BomType[], deletedBOMs: BomType[]) => void
}

export const BOMList: React.FC<BOMListProps> = ({ boms, isEditMode, onUpdateBOM }) => {
	const [editableBOM, setEditableBOM] = React.useState<BomType[]>(boms)
	const [deletedBOMs, setDeletedBOMs] = React.useState<BomType[]>([])

	React.useEffect(() => {
		setEditableBOM(boms)
		setDeletedBOMs([])
	}, [boms])

	const margin: BomType = React.useMemo(() => {
		const idx = boms.findIndex(
			aBom => aBom.manufacturer === 'Judahs' && aBom.name.toLowerCase() === 'margin',
		)

		if (idx > 0) {
			return boms[idx]
		}
		return new Bom()
	}, [boms])

	const handleEdit = (id: string, field: keyof BomType, value: any) => {
		const updatedBOM = editableBOM.map(item =>
			item.id === id ? { ...item, [field]: value } : item,
		)
		setEditableBOM(updatedBOM)
	}

	const handleAdd = () => {
		const newItem: BomType = {
			id: `new-${Date.now()}`,
			productId: '',
			manufacturer: '',
			name: '',
			jlcpcbNumber: '',
			link: '',
			unitUsd: 0,
			quantity: 1,
			useYn: 1,
			updatedBy: '',
			updatedAt: new Date().toISOString(),
			createdBy: '',
			createdAt: new Date().toISOString(),
		}
		setEditableBOM([...editableBOM, newItem])
	}

	const handleDelete = (id: string) => {
		const deletedBOM = editableBOM.find(item => item.id === id)
		if (deletedBOM) {
			setDeletedBOMs([...deletedBOMs, deletedBOM])
		}
		setEditableBOM(editableBOM.filter(item => item.id !== id))
	}

	const handleSave = () => {
		onUpdateBOM(editableBOM, deletedBOMs)
	}

	const totalCost = editableBOM
		.filter(item => item.useYn === 1)
		.reduce((sum, item) => sum + item.unitUsd * item.quantity, 0)

	return (
		<View css={style}>
			<TableContainer component={Paper} elevation={3} className="bomTableContainer">
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell className="headerCell">제조사</TableCell>
							<TableCell className="headerCell">제품명</TableCell>
							<TableCell className="headerCell">JLCPCB 넘버</TableCell>
							<TableCell className="headerCell">수량</TableCell>
							<TableCell className="headerCell">단가 (USD)</TableCell>
							<TableCell className="headerCell">원재가 (USD)</TableCell>
							<TableCell className="headerCell">링크</TableCell>
							<TableCell className="headerCell useYn">사용여부</TableCell>
							{isEditMode && <TableCell className="headerCell">삭제</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{editableBOM.map(row => (
							<TableRow key={row.id} className={`tableRow ${isEditMode ? 'edit' : ''}`}>
								<TableCell>
									{isEditMode ? (
										<TextField
											value={row.manufacturer}
											onChange={e => handleEdit(row.id, 'manufacturer', e.target.value)}
											variant="outlined"
											size="small"
											fullWidth
										/>
									) : (
										row.manufacturer
									)}
								</TableCell>
								<TableCell>
									{isEditMode ? (
										<TextField
											value={row.name}
											onChange={e => handleEdit(row.id, 'name', e.target.value)}
											variant="outlined"
											size="small"
											fullWidth
										/>
									) : (
										row.name
									)}
								</TableCell>
								<TableCell>
									{isEditMode ? (
										<TextField
											value={row.jlcpcbNumber}
											onChange={e => handleEdit(row.id, 'jlcpcbNumber', e.target.value)}
											variant="outlined"
											size="small"
											fullWidth
										/>
									) : (
										row.jlcpcbNumber
									)}
								</TableCell>
								<TableCell css={{ textAlign: 'right' }}>
									{isEditMode ? (
										<TextField
											type="number"
											value={row.quantity}
											onChange={e =>
												handleEdit(row.id, 'quantity', Number(e.target.value))
											}
											variant="outlined"
											size="small"
											inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }}
										/>
									) : (
										row.quantity
									)}
								</TableCell>
								<TableCell css={{ textAlign: 'right' }}>
									{isEditMode ? (
										<TextField
											type="number"
											value={row.unitUsd}
											onChange={e =>
												handleEdit(row.id, 'unitUsd', Number(e.target.value))
											}
											variant="outlined"
											size="small"
											inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
										/>
									) : (
										`${row.unitUsd.toLocaleString('en-US', {
											style: 'currency',
											currency: 'USD',
										})}`
									)}
								</TableCell>
								<TableCell css={{ textAlign: 'right' }}>
									{`${(row.unitUsd * row.quantity).toLocaleString('en-US', {
										style: 'currency',
										currency: 'USD',
									})}`}
								</TableCell>
								<TableCell>
									{isEditMode ? (
										<TextField
											value={row.link}
											onChange={e => handleEdit(row.id, 'link', e.target.value)}
											variant="outlined"
											size="small"
											fullWidth
										/>
									) : (
										<>
											{row.link && (
												<Link
													href={row.link}
													target="_blank"
													rel="noopener noreferrer"
													className="link"
												>
													링크
												</Link>
											)}
										</>
									)}
								</TableCell>
								<TableCell
									className="useYn"
									style={{ minWidth: isEditMode ? '3rem' : '1rem' }}
								>
									{isEditMode ? (
										<TextField
											select
											value={row.useYn}
											onChange={e => handleEdit(row.id, 'useYn', Number(e.target.value))}
											SelectProps={{ native: true }}
											variant="outlined"
											size="small"
										>
											<option value={1}>Yes</option>
											<option value={0}>No</option>
										</TextField>
									) : (
										<View css={{ textAlign: 'center' }}>
											{row.useYn === 1 && (
												<Icon
													name="check-bold"
													css={{ fontSize: '11px', color: blue['A400'] }}
												/>
											)}
										</View>
									)}
								</TableCell>
								{isEditMode && (
									<TableCell>
										<Tooltip title="Delete">
											<IconButton
												onClick={() => handleDelete(row.id)}
												size="small"
												color="error"
											>
												<DeleteIcon />
											</IconButton>
										</Tooltip>
									</TableCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{isEditMode && (
				<Button
					variant="contained"
					color="inherit"
					startIcon={<AddIcon />}
					onClick={handleAdd}
					className="addButton"
					size="small"
				>
					BOM 추가
				</Button>
			)}

			<View className="summaryView">
				<View className="summaryDataView">
					<Text category="h6" className="summaryLabel">
						제품원가:
					</Text>
					<Text category="h6" className="summaryValue">
						{(totalCost - margin.unitUsd).toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</Text>
				</View>

				<View className="summaryDataView">
					<Text category="h6" className="summaryLabel">
						영업이익:
					</Text>
					<Text category="h6" className="summaryValue">
						{margin.unitUsd.toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</Text>
				</View>

				<View className="summaryDataView">
					<Text category="h6" className="summaryLabel">
						VAT:
					</Text>
					<Text category="h6" className="summaryValue">
						{(totalCost / 10).toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</Text>
				</View>

				<View className="summaryDataView">
					<Text category="h6" className="summaryLabel">
						실판매가:
					</Text>
					<Text category="h6" className="summaryValue">
						{(totalCost + totalCost / 10).toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</Text>
				</View>

				<View className="summaryDataView">
					<Text category="h6" className="summaryLabel">
						영업이익률:
					</Text>
					<Text category="h6" className="summaryValue">
						{`${Decimal((margin.unitUsd / totalCost) * 100)}%`}
					</Text>
				</View>
			</View>

			{isEditMode && (
				<Button
					variant="contained"
					color="primary"
					onClick={handleSave}
					className="saveButton"
					size="large"
				>
					저장
				</Button>
			)}
		</View>
	)
}

export default BOMList

const style = css({
	marginTop: '20px',
	display: 'flex',
	flexDirection: 'column',
	gap: '20px',
	height: 'fit-content',
	overflow: 'hidden',

	'.bomTableContainer': {
		flex: 1,
		overflow: 'auto',
		width: '100%',
		height: 'fit-content',
		boxShadow: 'none',

		'.headerCell': {
			position: 'sticky',
			top: 0,
			zIndex: 1,
			padding: '0.3rem 0.5rem',
			backgroundColor: '#f5f5f5 !important',
			fontWeight: 'bold',
			fontSize: '11px',
		},

		'.tableRow': {
			'.MuiTableCell-body': {
				padding: '0.3rem 1rem',
				fontSize: '11px',
				borderBottom: 0,

				'.MuiOutlinedInput-root': {
					'.MuiInputBase-input:not(.MuiNativeSelect-select)': {
						padding: '0.3rem',
						fontSize: '11px',
					},
					'.MuiNativeSelect-select': {
						paddingTop: '0.3rem',
						paddingBottom: '0.3rem',
						paddingLeft: '0.5rem',
						fontSize: '11px',
						lineHeight: 'normal',
					},

					'.MuiOutlinedInput-notchedOutline': {
						borderColor: grey[200],
					},
				},
			},
			'&:nth-of-type(odd)': {
				backgroundColor: grey[50],
			},
			'&:hover': {
				backgroundColor: grey[100],
			},

			'&.edit': {
				'.MuiTableCell-body': {
					padding: '0 0.1rem',
				},
			},
		},

		'.link': {
			textDecoration: 'none',
			color: blue['A400'],

			'&:hover': {
				textDecoration: 'underline',
			},
		},
	},

	'.addButton': {
		alignSelf: 'flex-start',
	},

	'.summaryView': {
		paddingTop: '1rem',
		borderTop: '1px solid',
		borderColor: grey[400],

		'.summaryDataView': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			justifySelf: 'end',
			width: '20rem',

			'.summaryLabel': {
				marginTop: '0.3rem',
				marginBottom: '0.3rem',
				alignSelf: 'flex-end',
			},

			'.summaryValue': {
				marginTop: '0.3rem',
				marginBottom: '0.3rem',
				color: blue['A400'],
				fontWeight: 'bold',
			},
		},
	},

	'.saveButton': {
		alignSelf: 'flex-end',
	},
})
